// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ContactTypography from "@hardwoods/App/Components/Common/ContactTypography";
import Call from "@hardwoods/XDL/Icons/Call";
import Email from "@hardwoods/XDL/Icons/Email";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import { emailValidation, phoneNumberValidation } from "@hardwoods/XDL/Utilities/validation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FooterContext } from "@insite/content-library/Pages/Footer";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    phoneNumber = "phoneNumber",
    emailAddress = "emailAddress",
}

interface OwnProps extends WidgetProps {
    extendedStyles?: ContactUsStyles;
    fields: {
        [fields.phoneNumber]: string;
        [fields.emailAddress]: string;
    };
}

export interface ContactUsStyles {
    widgetWrapper?: InjectableCss;
    item?: any;
}

export const contactUsStyles: ContactUsStyles = {
    widgetWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            > div {
                margin-bottom: 8px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    item: {
        wrapper: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
        icon: {
            size: 16,
            color: "common.white",
        },
        description: {
            size: 14,
            color: "common.white",
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 14px;
            `,
        },
    },
};

const ContactUs: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(contactUsStyles, extendedStyles));
    const getIcon = (icon: React.ComponentType | string) => {
        return {
            iconProps: {
                src: icon,
                ...styles.item.icon,
            },
            position: "left",
        };
    };

    return (
        <StyledWrapper {...styles.widgetWrapper}>
            {fields.phoneNumber && (
                <StyledWrapper {...styles.item.wrapper}>
                    <ContactTypography
                        targetValue={fields.phoneNumber}
                        type="phone"
                        icon={getIcon(Call)}
                        {...styles.item.description}
                    >
                        {fields.phoneNumber}
                    </ContactTypography>
                </StyledWrapper>
            )}
            {fields.emailAddress && (
                <StyledWrapper {...styles.item.wrapper}>
                    <ContactTypography
                        targetValue={fields.emailAddress}
                        type="email"
                        icon={getIcon(Email)}
                        {...styles.item.description}
                    >
                        {translate("Message Us")}
                    </ContactTypography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const contactUs: WidgetModule = {
    component: ContactUs,
    definition: {
        group: "Footer",
        allowedContexts: [FooterContext],
        fieldDefinitions: [
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
                validate: value =>
                    phoneNumberValidation(value) ? null : translate("Please enter a valid phone number."),
            },
            {
                name: fields.emailAddress,
                displayName: "Email Address",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
                validate: value => (emailValidation(value) ? null : translate("Please enter a valid email.")),
            },
        ],
    },
};

export default contactUs;
